import {formatDobShort, isNonEnglish} from '../../constants/designTokens';
import {
  foldSearchText,
  matchesSearchText,
  searchTextMatchedTokens,
} from '../../utils/searchText';
import {patientDisplayNameFromRecord} from '../../utils/patientNames';
import {plannerEventDate} from './plannerDateTime';
import {buildPlannerPatientSelectionEvent} from './plannerPatientBrowser';
import {plannerEventSummarySnippet} from './plannerSearchSnippets';
import {
  clean,
  plannerPatientIdentityKey,
  plannerPatientIdentityKeys,
  plannerPatientSourceLabel,
  plannerPatientToPatientItem,
} from './plannerModels';

const MAX_VISIBLE_ENCOUNTER_HITS = 3;
const SEARCH_RESULT_TYPES = Object.freeze({
  patientGroup: 'patientGroup',
  standaloneEventRow: 'standaloneEventRow',
});

export const plannerSearchEventTimestampValue = (event: any) =>
  plannerEventDate(event)?.getTime?.() ?? 0;

export const sortPlannerSearchEventsNewestFirst = (events: any[] = []) =>
  [...events].sort(
    (left, right) =>
      plannerSearchEventTimestampValue(right) -
      plannerSearchEventTimestampValue(left),
  );

export const sortPlannerSearchEventsSoonestFirst = (events: any[] = []) =>
  [...events].sort((left, right) => {
    const leftValue =
      plannerEventDate(left)?.getTime?.() ?? Number.MAX_SAFE_INTEGER;
    const rightValue =
      plannerEventDate(right)?.getTime?.() ?? Number.MAX_SAFE_INTEGER;
    return leftValue - rightValue;
  });

const patientNameFromRecord = (patient: any = {}) => {
  return patientDisplayNameFromRecord(patient || {});
};

const patientLocalId = (patient: any = {}) =>
  clean(patient.localId) ||
  (clean(patient.id).startsWith('local_')
    ? clean(patient.id).slice('local_'.length)
    : '');

const patientEhrId = (patient: any = {}) =>
  clean(patient.ehrId) ||
  (clean(patient.id).startsWith('ehr_')
    ? clean(patient.id).slice('ehr_'.length)
    : '');

const fallbackIdentityKey = (name: any) => {
  const normalizedName = foldSearchText(name)
    .trim()
    .replace(/[^\p{L}\p{N}]+/gu, '-');
  return normalizedName ? `name:${normalizedName}` : '';
};

const patientIdentityKeysForHeader = (header: any) => {
  const keys = plannerPatientIdentityKeys(
    {
      id: header.patientId,
      localId: header.localPatientId,
      ehrId: header.ehrPatientId,
    },
    {
      includePlannerId: false,
    },
  );
  if (keys.length) return keys;
  const fallback = fallbackIdentityKey(header.name);
  return fallback ? [fallback] : [];
};

export const searchEventPatientIdentityKeys = (event: any) =>
  plannerPatientIdentityKeys(event?.patient, {
    fallbackKey: fallbackIdentityKey(event?.patientName),
  });

export const searchEventPatientIdentityKey = (event: any) =>
  plannerPatientIdentityKey(event?.patient, {
    fallbackKey: fallbackIdentityKey(event?.patientName),
  });

const hasValue = (value: any) => clean(value) !== '';

const fillMissingAliasGroup = (record: any, aliases: string[], value: any) => {
  if (!record || typeof record !== 'object' || !hasValue(value)) return record;
  if (aliases.some(alias => hasValue(record[alias]))) return record;

  return aliases.reduce(
    (next, alias) => ({
      ...next,
      [alias]: value,
    }),
    {...record},
  );
};

const enrichPatientRecordWithHeader = (
  record: any,
  header: any,
  sourceKind = 'ehr',
) => {
  if (!record || !header) return record;
  const languageAliases =
    sourceKind === 'local'
      ? ['preferred_language', 'preferredLanguage', 'language']
      : ['language', 'preferred_language', 'preferredLanguage'];
  const dobAliases =
    sourceKind === 'local'
      ? ['date_of_birth', 'dateOfBirth', 'dob', 'birthDate']
      : ['dob', 'dateOfBirth', 'date_of_birth', 'birthDate'];

  return fillMissingAliasGroup(
    fillMissingAliasGroup(
      fillMissingAliasGroup(record, languageAliases, header.language),
      ['mrn', 'medicalRecordNumber'],
      header.mrn,
    ),
    dobAliases,
    header.dob,
  );
};

const enrichPatientItemWithHeader = (item: any, header: any) => {
  if (!item || !header) return item;
  const hasPatientItemShape =
    Object.prototype.hasOwnProperty.call(item, 'localPatient') ||
    Object.prototype.hasOwnProperty.call(item, 'ehrPatient');
  if (!hasPatientItemShape) {
    return enrichPatientRecordWithHeader(item, header, 'local');
  }

  return {
    ...item,
    localPatient: item.localPatient
      ? enrichPatientRecordWithHeader(item.localPatient, header, 'local')
      : item.localPatient,
    ehrPatient: item.ehrPatient
      ? enrichPatientRecordWithHeader(item.ehrPatient, header, 'ehr')
      : item.ehrPatient,
  };
};

const buildPatientHeaderFromEvent = (event: any, selectionItem: any = null) => {
  if (!event) return null;
  const patient = event.patient || {};
  const name = patientNameFromRecord(patient) || clean(event.patientName);
  if (!name) return null;

  const localPatientId = patientLocalId(patient);
  const ehrPatientId = patientEhrId(patient);
  if (!localPatientId && !ehrPatientId && !clean(patient.id)) return null;
  const nameParts = name.split(/\s+/);
  const patientId =
    clean(patient.id) ||
    (localPatientId ? `local_${localPatientId}` : '') ||
    (ehrPatientId ? `ehr_${ehrPatientId}` : '') ||
    '';
  const headerLanguage = clean(patient.language);
  const headerDialect = clean(
    patient.dialect || event.dialect || event.patient?.dialect,
  );
  const headerDob = patient.dob || null;
  const headerMrn = clean(patient.mrn);
  const sourceLabel = plannerPatientSourceLabel(patient);
  const selectionPatient = {
    ...patient,
    id: patientId || patient.id,
    localId: patient.localId || localPatientId || null,
    ehrId: patient.ehrId || ehrPatientId || null,
    firstName: patient.firstName || nameParts[0] || '',
    middleName: patient.middleName || '',
    lastName: patient.lastName || nameParts.slice(1).join(' '),
    language: headerLanguage,
    dialect: headerDialect,
    mrn: headerMrn,
    dob: headerDob,
    linkedEhrId: patient.linkedEhrId || null,
  };
  const header = {
    name,
    language: headerLanguage,
    dialect: headerDialect,
    dob: headerDob,
    mrn: headerMrn,
    sourceLabel,
    localPatientId: localPatientId || null,
    ehrPatientId: ehrPatientId || null,
    linkedEhrId: patient.linkedEhrId || null,
    patientId: patientId || null,
    patient,
    selectionItem:
      selectionItem || plannerPatientToPatientItem(selectionPatient),
  };

  return {
    ...header,
    selectionItem: enrichPatientItemWithHeader(header.selectionItem, header),
  };
};

const buildPatientHeaderFromItem = (item: any, source: any) => {
  const event = buildPlannerPatientSelectionEvent(item, {source});
  return buildPatientHeaderFromEvent(event, item);
};

const headerDetailScore = (header: any) =>
  [header?.name, header?.language, header?.dialect, header?.dob, header?.mrn]
    .map(clean)
    .filter(Boolean).length;

const mergeHeaders = (current: any, incoming: any) => {
  if (!incoming) return current;
  if (!current) return incoming;

  const preferred =
    headerDetailScore(incoming) > headerDetailScore(current)
      ? incoming
      : current;
  const fallback = preferred === incoming ? current : incoming;
  const merged = {
    ...fallback,
    ...preferred,
    name: preferred.name || fallback.name,
    language: preferred.language || fallback.language || '',
    dialect: preferred.dialect || fallback.dialect || '',
    dob: preferred.dob || fallback.dob || null,
    mrn: preferred.mrn || fallback.mrn || '',
    sourceLabel: preferred.sourceLabel || fallback.sourceLabel || '',
    localPatientId: preferred.localPatientId || fallback.localPatientId || null,
    ehrPatientId: preferred.ehrPatientId || fallback.ehrPatientId || null,
    linkedEhrId: preferred.linkedEhrId || fallback.linkedEhrId || null,
    patientId: preferred.patientId || fallback.patientId || null,
    patient: {
      ...(fallback.patient || {}),
      ...(preferred.patient || {}),
    },
    selectionItem: current.selectionItem || incoming.selectionItem,
  };

  return {
    ...merged,
    selectionItem: enrichPatientItemWithHeader(merged.selectionItem, merged),
  };
};

const patientHeaderSearchText = (header: any) => {
  if (!header) return '';
  const dobShort = formatDobShort(header.dob);
  return [
    header.name,
    header.sourceLabel,
    isNonEnglish(header.language) ? header.language : '',
    dobShort ? `DOB ${dobShort}` : '',
    header.mrn ? `MRN ${header.mrn}` : '',
  ]
    .map(clean)
    .filter(Boolean)
    .join(' ');
};

const visibleEncounterKeywordText = (event: any, query: any) =>
  clean(plannerEventSummarySnippet(event, query));

const eventPatientName = (event: any) =>
  patientNameFromRecord(event?.patient) || clean(event?.patientName);

const eventSortName = (event: any) =>
  (eventPatientName(event) || clean(event?.statusLabel) || clean(event?.source))
    .toLowerCase()
    .trim();

const eventSearchText = (event: any, query: any) => {
  const patient = event?.patient || {};
  const dobShort = formatDobShort(patient.dob);
  return [
    eventPatientName(event),
    event?.patientName,
    patient.firstName,
    patient.middleName,
    patient.lastName,
    plannerPatientSourceLabel(patient),
    event?.language,
    patient.language,
    dobShort ? `DOB ${dobShort}` : '',
    patient.mrn ? `MRN ${patient.mrn}` : '',
    event?.statusLabel,
    event?.detailLine,
    event?.appointmentSummary,
    event?.summaryText,
    event?.summarySearchText,
    ...(Array.isArray(event?.summaryVariants) ? event.summaryVariants : []),
    visibleEncounterKeywordText(event, query),
    event?.source,
  ]
    .map(clean)
    .filter(Boolean)
    .join('\n');
};

const isStandaloneSearchEvent = (event: any) =>
  Boolean(
    event &&
    (event.source === 'phoneCall' ||
      event.source === 'scheduledBot' ||
      !event.patient),
  );

const sourceEventKey = (event: any) =>
  clean(event?.sourceId) || clean(event?.id);

const uniqueBySourceEvent = (events: any[] = []) => {
  const seen = new Set();
  return events.filter(event => {
    const key = sourceEventKey(event);
    if (!key) return true;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const newestTime = (events: any[] = []) =>
  events.reduce(
    (latest, event) =>
      Math.max(latest, plannerSearchEventTimestampValue(event)),
    0,
  );

const resultSortName = (result: any) =>
  result?.type === SEARCH_RESULT_TYPES.standaloneEventRow
    ? eventSortName(result.event)
    : clean(result?.patientHeader?.name).toLowerCase();

const createSearchGroup = (header: any, order: number) => ({
  type: SEARCH_RESULT_TYPES.patientGroup,
  key: patientIdentityKeysForHeader(header).join('|') || `patient:${order}`,
  order,
  patientHeader: header,
  patientItems: [],
  scheduledEvents: [],
  recentEvents: [],
  encounterEvents: [],
  searchTextParts: [],
});

const addGroupAliases = (
  groupMap: Map<string, any>,
  group: any,
  header: any,
) => {
  patientIdentityKeysForHeader(header).forEach(identityKey => {
    groupMap.set(identityKey, group);
  });
};

const upsertGroup = (
  groupMap: Map<string, any>,
  groups: any[],
  header: any,
) => {
  if (!header) return null;
  const identityKeys = patientIdentityKeysForHeader(header);
  let group = identityKeys
    .map(identityKey => groupMap.get(identityKey))
    .find(Boolean);
  if (!group) {
    group = createSearchGroup(header, groups.length);
    groups.push(group);
  } else {
    group.patientHeader = mergeHeaders(group.patientHeader, header);
  }
  addGroupAliases(groupMap, group, group.patientHeader);
  return group;
};

const addPatientItem = ({
  groupMap,
  groups,
  item,
  source,
}: Record<string, any>) => {
  const header = buildPatientHeaderFromItem(item, source);
  const group = upsertGroup(groupMap, groups, header);
  if (!group) return;
  group.patientItems.push(item);
  group.searchTextParts.push(patientHeaderSearchText(group.patientHeader));
};

const addScheduledEvent = ({groupMap, groups, event}: Record<string, any>) => {
  const header = buildPatientHeaderFromEvent(event);
  const group = upsertGroup(groupMap, groups, header);
  if (!group) return;
  group.scheduledEvents.push(event);
  group.searchTextParts.push(patientHeaderSearchText(group.patientHeader));
};

const addRecentEvent = ({
  groupMap,
  groups,
  event,
  query,
}: Record<string, any>) => {
  const header = buildPatientHeaderFromEvent(event);
  const group = upsertGroup(groupMap, groups, header);
  if (!group) return;
  group.recentEvents.push(event);
  group.searchTextParts.push(patientHeaderSearchText(group.patientHeader));

  const keywordText = visibleEncounterKeywordText(event, query);
  if (matchesSearchText(keywordText, query)) {
    group.encounterEvents.push(event);
    group.searchTextParts.push(keywordText);
  }
};

const addEncounterKeywordEvent = ({
  groupMap,
  groups,
  event,
  query,
}: Record<string, any>) => {
  const header = buildPatientHeaderFromEvent(event);
  const group = upsertGroup(groupMap, groups, header);
  if (!group) return;
  group.searchTextParts.push(patientHeaderSearchText(group.patientHeader));

  const keywordText = visibleEncounterKeywordText(event, query);
  if (!matchesSearchText(keywordText, query)) return;

  group.encounterEvents.push(event);
  group.searchTextParts.push(keywordText);
};

const finalizeSearchGroup = (group: any, query: any) => {
  const scheduledEvents = sortPlannerSearchEventsSoonestFirst(
    uniqueBySourceEvent(group.scheduledEvents),
  );
  const recentEvents = sortPlannerSearchEventsNewestFirst(
    uniqueBySourceEvent(group.recentEvents),
  );
  const encounterEvents = sortPlannerSearchEventsNewestFirst(
    uniqueBySourceEvent(group.encounterEvents),
  );
  const searchText = [
    patientHeaderSearchText(group.patientHeader),
    ...group.searchTextParts,
  ].join('\n');
  const matchedTokens = searchTextMatchedTokens(searchText, query);
  if (!matchedTokens.length) return null;

  const visibleEncounterEvents = encounterEvents.slice(
    0,
    MAX_VISIBLE_ENCOUNTER_HITS,
  );
  return {
    key: group.key,
    type: SEARCH_RESULT_TYPES.patientGroup,
    patientHeader: mergeHeaders(group.patientHeader, null),
    matchCount: matchedTokens.length,
    matchedTokens,
    scheduledEvents,
    recentEvents,
    encounterEvents: visibleEncounterEvents,
    hiddenEncounterCount: Math.max(
      0,
      encounterEvents.length - visibleEncounterEvents.length,
    ),
    hasScheduled: scheduledEvents.length > 0,
    lastSeenAt: newestTime(recentEvents),
    latestEncounterKeywordAt: newestTime(encounterEvents),
    order: group.order,
  };
};

const finalizeStandaloneEvent = (event: any, query: any, order: number) => {
  const searchText = eventSearchText(event, query);
  const matchedTokens = searchTextMatchedTokens(searchText, query);
  if (!matchedTokens.length) return null;
  const timestamp = plannerSearchEventTimestampValue(event);
  const stage = clean(event?.stage);
  const keywordText = visibleEncounterKeywordText(event, query);
  const hasKeywordMatch = matchesSearchText(keywordText, query);

  return {
    key: `event:${sourceEventKey(event) || order}`,
    type: SEARCH_RESULT_TYPES.standaloneEventRow,
    order,
    event,
    matchCount: matchedTokens.length,
    matchedTokens,
    summarySnippet: hasKeywordMatch ? keywordText : '',
    hasScheduled: stage === 'upcoming',
    lastSeenAt: stage === 'completed' || stage === 'noShow' ? timestamp : 0,
    latestEncounterKeywordAt: hasKeywordMatch ? timestamp : 0,
  };
};

const addSearchEvent = ({
  event,
  events,
  groupMap,
  groups,
  query,
  sourceKind,
}: Record<string, any>) => {
  if (isStandaloneSearchEvent(event)) {
    events.push(event);
    return;
  }

  if (sourceKind === 'scheduled') {
    addScheduledEvent({groupMap, groups, event});
    return;
  }

  if (sourceKind === 'encounterKeyword') {
    addEncounterKeywordEvent({groupMap, groups, event, query});
    return;
  }

  addRecentEvent({groupMap, groups, event, query});
};

export const buildSearchPatientEncounterEntries = ({
  encounterEvents = [],
  items = [],
  query = '',
  recentEvents = [],
  scheduledEvents = [],
  source = 'search',
}: Record<string, any>) => {
  const trimmedQuery = clean(query);
  if (!trimmedQuery) return {results: [], groups: [], flatEvents: []};

  const groupMap = new Map();
  const groups: any[] = [];
  const standaloneEvents: any[] = [];

  items.forEach((item: any) =>
    addPatientItem({groupMap, groups, item, source}),
  );
  scheduledEvents.forEach((event: any) =>
    addSearchEvent({
      event,
      events: standaloneEvents,
      groupMap,
      groups,
      query: trimmedQuery,
      sourceKind: 'scheduled',
    }),
  );
  recentEvents.forEach((event: any) =>
    addSearchEvent({
      event,
      events: standaloneEvents,
      groupMap,
      groups,
      query: trimmedQuery,
      sourceKind: 'recent',
    }),
  );
  encounterEvents.forEach((event: any) =>
    addSearchEvent({
      event,
      events: standaloneEvents,
      groupMap,
      groups,
      query: trimmedQuery,
      sourceKind: 'encounterKeyword',
    }),
  );

  const patientGroupResults = groups
    .map(group => finalizeSearchGroup(group, trimmedQuery))
    .filter(Boolean);
  const eventResults = uniqueBySourceEvent(standaloneEvents)
    .map((event, index) => finalizeStandaloneEvent(event, trimmedQuery, index))
    .filter(Boolean);

  const results = [...patientGroupResults, ...eventResults]
    .filter(Boolean)
    .sort(
      (left: any, right: any) =>
        right.matchCount - left.matchCount ||
        Number(right.hasScheduled) - Number(left.hasScheduled) ||
        right.lastSeenAt - left.lastSeenAt ||
        right.latestEncounterKeywordAt - left.latestEncounterKeywordAt ||
        resultSortName(left).localeCompare(resultSortName(right)) ||
        left.order - right.order,
    );

  return {
    results,
    groups: results,
    flatEvents: eventResults.map((result: any) => result.event).filter(Boolean),
  };
};

export const searchResultIsSelected = (
  result: any,
  selectedEventId: any,
  selectedPatientId: any,
) => {
  if (result?.type === SEARCH_RESULT_TYPES.standaloneEventRow) {
    return Boolean(
      selectedEventId &&
      result?.event?.id &&
      result.event.id === selectedEventId,
    );
  }
  const header = result?.patientHeader || {};
  return Boolean(
    (selectedPatientId &&
      header.patientId &&
      header.patientId === selectedPatientId) ||
    (selectedEventId &&
      result?.encounterEvents?.some(
        (event: any) => event?.id === selectedEventId,
      )),
  );
};
